import { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'

// ── Centralized follows cache ────────────────────────────────────────────────
// Before: 8+ components each fetched the follow list independently on mount.
// Logs showed 5+ identical /follows queries firing within milliseconds per
// page load. This hook consolidates them — one fetch per user session,
// module-level cache, all consumers share the same array.

// The private circle labels, ONE definition. These had drifted into three separate
// copies (People.jsx, TonightTogether.jsx, InviteToWatchSheet.jsx) which is how the
// wording split — People said "Close friends" while the filters said "Close". Use
// `label` for a full row and `short` for a chip.
export const CIRCLES = [
  { id: 'family',  label: 'Family',        short: 'Family',  emoji: '👪' },
  { id: 'friends', label: 'Friends',       short: 'Friends', emoji: '🤝' },
  { id: 'close',   label: 'Close friends', short: 'Close',   emoji: '⭐' },
]
export const CIRCLE_BY_ID = Object.fromEntries(CIRCLES.map(c => [c.id, c]))

const cache = new Map()        // user_id → { ids: string[], vipIds: string[] }
const pending = new Map()      // user_id → Promise<{ ids, vipIds }>
const subscribers = new Set()  // setState fns to notify on invalidation
let tick = 0

async function fetchFollows(userId) {
  if (cache.has(userId)) return cache.get(userId)
  if (pending.has(userId)) return pending.get(userId)

  const p = supabase.from('follows')
    .select('following_id, is_vip, circle')
    .eq('follower_id', userId)
    .then(({ data }) => {
      const rows = data || []
      // circles: { following_id → 'family'|'friends'|'close' } — private label from
      // People, now also available to circle-aware surfaces (e.g. Tonight filter).
      const circles = {}
      rows.forEach(r => { if (r.circle) circles[r.following_id] = r.circle })
      const val = { ids: rows.map(r => r.following_id), vipIds: rows.filter(r => r.is_vip).map(r => r.following_id), circles }
      cache.set(userId, val)
      pending.delete(userId)
      return val
    })
    .catch(() => {
      pending.delete(userId)
      return { ids: [], vipIds: [], circles: {} }
    })

  pending.set(userId, p)
  return p
}

// Bust the cache for a user and re-notify every live consumer — call after a
// follow/unfollow or a VIP toggle so the shared list stays fresh in-session.
export function invalidateFollows(userId) {
  cache.delete(userId)
  pending.delete(userId)
  tick += 1
  subscribers.forEach(fn => fn(tick))
}

export function useFollows(userId) {
  const [state, setState] = useState(() => cache.get(userId) || { ids: [], vipIds: [], circles: {} })
  const [ready, setReady] = useState(() => cache.has(userId))
  const tickRef = useRef(0)

  useEffect(() => {
    if (!userId) { setState({ ids: [], vipIds: [], circles: {} }); setReady(true); return }
    let cancelled = false
    fetchFollows(userId).then(v => {
      if (cancelled) return
      setState(v)
      setReady(true)
    })
    // Subscribe to invalidations
    const onInvalidate = (t) => {
      if (t === tickRef.current) return
      tickRef.current = t
      fetchFollows(userId).then(v => { if (!cancelled) setState(v) })
    }
    subscribers.add(onInvalidate)
    return () => { cancelled = true; subscribers.delete(onInvalidate) }
  }, [userId])

  return { followingIds: state.ids, vipIds: state.vipIds, circles: state.circles || {}, ready }
}
